import React, { useState } from "react";
import { validateFile } from "../utils/helper";
import { postMultipart } from "../utils/api";

const MAX_IMAGES = 10;

const MultiUpload = ({ images = [], onChange, labelText = "Add Images", uploadEndpoint = null }) => {
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    setError(null);
    const files = Array.from(e.target.files || []);
    const available = MAX_IMAGES - images.length;

    if (available <= 0) {
      setError(`Maximum ${MAX_IMAGES} images allowed.`);
      e.target.value = "";
      return;
    }

    const toProcess = files.slice(0, available);
    setUploading(true);
    try {
      const results = await Promise.all(
        toProcess.map(async (file) => {
          const err = validateFile(file);
          if (err) throw new Error(err);
          if (uploadEndpoint) {
            const fd = new FormData();
            fd.append("image", file);
            const data = await postMultipart(uploadEndpoint, fd);
            return data.url;
          }
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
          });
        })
      );
      onChange([...images, ...results]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {images.map((src, i) => (
          <div key={i} className="relative w-24 h-24 border rounded-lg overflow-hidden flex-shrink-0">
            <img src={src} alt={`Property ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="absolute top-0 right-0 bg-white rounded-full w-5 h-5 flex items-center justify-center text-gray-500 hover:text-red-500 shadow"
            >
              &times;
            </button>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              id="multi-image-upload"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <label htmlFor="multi-image-upload" className="cursor-pointer text-center p-2">
              {uploading ? (
                <span className="text-[10px] text-gray-400">Uploading...</span>
              ) : (
                <>
                  <span className="material-symbols-outlined text-gray-400 text-3xl">add_photo_alternate</span>
                  <p className="text-[10px] text-gray-400 mt-1">{labelText}</p>
                </>
              )}
            </label>
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default MultiUpload;
